import React, { createContext, useContext, useRef, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import styled, { keyframes, css } from 'styled-components';

// ─── Keyframes ────────────────────────────────────────────────────────────────

const fadeIn = keyframes`
  from { opacity: 0; }
  to   { opacity: 1; }
`;

const fadeOut = keyframes`
  from { opacity: 1; }
  to   { opacity: 0; }
`;

const spin = keyframes`
  from { transform: rotate(0deg); }
  to   { transform: rotate(360deg); }
`;

// ─── Styled Components ────────────────────────────────────────────────────────

const Overlay = styled.div<{ $phase: 'in' | 'out' }>`
  position: fixed;
  inset: 0;
  z-index: 9999;
  background-color: #F3EFE6;
  display: flex;
  align-items: center;
  justify-content: center;
  pointer-events: all;

  ${({ $phase }) =>
    $phase === 'in'
      ? css`animation: ${fadeIn} 0.35s ease forwards;`
      : css`animation: ${fadeOut} 0.4s ease forwards;`}
`;

const Logo = styled.img`
  width: 90px;
  height: 90px;
  object-fit: contain;
  animation: ${spin} 1.1s linear infinite;
`;

// ─── Timing constants (ms) ────────────────────────────────────────────────────

const FADE_IN_MS  = 350;  // overlay 페이드 인 시간
const HOLD_MS     = 300;  // 로고 보여주는 유지 시간
const FADE_OUT_MS = 400;  // 페이드 아웃 시간

// ─── Context ──────────────────────────────────────────────────────────────────

interface TransitionContextValue {
  navigateTo: (path: string) => void;
}

const TransitionContext = createContext<TransitionContextValue>({
  navigateTo: () => {},
});

export const useTransitionNavigate = () => useContext(TransitionContext);

// ─── Provider ─────────────────────────────────────────────────────────────────

export const TransitionProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const navigate = useNavigate();
  const [visible, setVisible] = useState(false);
  const [phase, setPhase]     = useState<'in' | 'out'>('in');
  const timerRef = useRef<NodeJS.Timeout[]>([]);

  const clearTimers = () => {
    timerRef.current.forEach(clearTimeout);
    timerRef.current = [];
  };

  const navigateTo = useCallback((path: string) => {
    clearTimers();

    // 1) 오버레이 페이드 인
    setPhase('in');
    setVisible(true);

    // 2) 페이드 인 완료 후 → 페이지 이동 + 잠깐 유지
    const t1 = setTimeout(() => {
      navigate(path);
      window.scrollTo(0, 0);
    }, FADE_IN_MS);

    // 3) 유지 후 → 오버레이 페이드 아웃 시작
    const t2 = setTimeout(() => {
      setPhase('out');
    }, FADE_IN_MS + HOLD_MS);

    // 4) 완전히 사라짐 → 언마운트
    const t3 = setTimeout(() => {
      setVisible(false);
    }, FADE_IN_MS + HOLD_MS + FADE_OUT_MS);

    timerRef.current = [t1, t2, t3];
  }, [navigate]);

  return (
    <TransitionContext.Provider value={{ navigateTo }}>
      {children}
      {visible && (
        <Overlay $phase={phase}>
          <Logo src="/logo/Allta_fingerprint-V2-1.png" alt="Allta loading" />
        </Overlay>
      )}
    </TransitionContext.Provider>
  );
};
